import { useCallback, useEffect, useRef, useState } from 'react';

import type { UseXoeRailStateOptions, XoeRailState } from '../../types/xoe/XoeUiTypes';

export const XOE_ARCHIVE_UNDO_MS = 8000;

const readPersisted = (key: string | undefined, fallback: boolean): boolean => {
	if (!key || typeof window === 'undefined') return fallback;
	try {
		const stored = window.localStorage.getItem(key);
		return stored === null ? fallback : stored === 'true';
	} catch {
		return fallback;
	}
};

export const useXoeRailState = ({ session, persistKey, defaultCollapsed }: UseXoeRailStateOptions): XoeRailState => {
	const [collapsed, setCollapsed] = useState(() => readPersisted(persistKey, defaultCollapsed));

	useEffect(() => {
		if (!persistKey) return;
		try {
			window.localStorage.setItem(persistKey, String(collapsed));
		} catch {
			// Storage can be blocked (private mode); the rail still works for this visit.
		}
	}, [persistKey, collapsed]);

	const toggle = useCallback(() => setCollapsed((prev) => !prev), []);

	const [undoUuid, setUndoUuid] = useState<string | null>(null);
	const undoTimerRef = useRef<number | null>(null);

	const clearUndoTimer = useCallback(() => {
		if (undoTimerRef.current !== null) {
			window.clearTimeout(undoTimerRef.current);
			undoTimerRef.current = null;
		}
	}, []);

	useEffect(() => clearUndoTimer, [clearUndoTimer]);

	const { archiveConversation, renameConversation, deleteConversation } = session;

	const archive = useCallback(
		(uuid: string) => {
			void archiveConversation(uuid, true);
			clearUndoTimer();
			setUndoUuid(uuid);
			undoTimerRef.current = window.setTimeout(() => setUndoUuid(null), XOE_ARCHIVE_UNDO_MS);
		},
		[archiveConversation, clearUndoTimer],
	);

	const undo = useCallback(() => {
		if (!undoUuid) return;
		void archiveConversation(undoUuid, false);
		clearUndoTimer();
		setUndoUuid(null);
	}, [undoUuid, archiveConversation, clearUndoTimer]);

	const rename = useCallback(
		(uuid: string, title: string) => {
			void renameConversation(uuid, title);
		},
		[renameConversation],
	);

	const remove = useCallback(
		(uuid: string) => {
			void deleteConversation(uuid);
		},
		[deleteConversation],
	);

	return { collapsed, setCollapsed, toggle, undoUuid, archive, undo, rename, remove };
};
